import { Callout } from '@assembly-js/design-system';
import { Button, MenuItem } from '@mui/material';
import { FieldArray, FormikProps } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import {
  ModuleSettingsItem,
  VisibilityRule,
  VisibilityRuleType,
} from 'src/constants';
import { FormModal } from 'src/legacy/components/Modals';
import RowDivider from 'src/legacy/components/RowDivider';
import BaseTypography, {
  typography13MediumStyle,
} from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';
import { BlackSmall, DarkFont, NonHoverBorder } from 'src/theme/colors';
import {
  AllVisibilityRules,
  applyVisibilityTypeChange,
  getDefaultVisibilityRule,
  isVisibilityType,
  rulesSchema,
  VisibilityType,
} from 'src/utils/app_visibility';
import { generateUniqueId } from 'src/utils/common_utils';

import { AppVisibilityRule } from './AppVisibilityRule';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    padding: theme.spacing(2.5, 3.5, 3.5, 3.5),
  },
  title: {
    marginBottom: theme.spacing(0.75),
  },
  rulesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  addRuleBtn: {
    color: BlackSmall,
    border: `1px solid ${NonHoverBorder}`,
    padding: theme.spacing(0.25, 1),
    ...typography13MediumStyle,
  },
}));

type Props = {
  onCloseModal: () => void;
  open: boolean;
  moduleVisbilityConfig: VisibilityRule[];
  moduleSettings: ModuleSettingsItem[];
  onVisibilityConfigSaved: (values: AllVisibilityRules) => void | Promise<void>;
};

export const useVisibilityForm = () => {
  return {
    FormRenderer: VisibilityFormRenderer,
    validationScheme: toFormikValidationSchema(rulesSchema),
  };
};

const VisibilityFormRenderer = ({
  values,
  handleBlur,
  handleChange,
  setFieldValue,
}: FormikProps<AllVisibilityRules>) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const handleVisibility = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isVisibilityType(event.target.value)) {
      applyVisibilityTypeChange(event.target.value, setFieldValue);
      handleChange(event);
    }
  };

  return (
    <div className={classes.root}>
      <Callout description={t(($) => $.page_apps.app_visibility_description)} />
      <div>
        <div className={classes.title}>
          <BaseTypography fontType="13Medium" textColor={DarkFont}>
            Visibility
          </BaseTypography>
        </div>
        <BaseTextField
          fullWidth
          select
          sizeVariant="medium"
          variant="outlined"
          onChange={handleVisibility}
          onBlur={handleBlur}
          value={values.visibility}
          name="visibility"
          defaultValue={VisibilityType.VisibleToEveryone}
        >
          <MenuItem value={VisibilityType.CustomVisibility}>
            {t(($) => $.page_apps.client_facing_app_visibility.subtitle.custom)}
          </MenuItem>
          <MenuItem value={VisibilityType.NoVisibility}>
            {t(($) => $.page_apps.client_facing_app_visibility.subtitle.none)}
          </MenuItem>
          <MenuItem value={VisibilityType.VisibleToEveryone}>
            {t(
              ($) =>
                $.page_apps.client_facing_app_visibility.subtitle.all_clients,
            )}
          </MenuItem>
        </BaseTextField>
      </div>
      {values.visibility === VisibilityType.CustomVisibility && (
        <div className={classes.rulesContainer}>
          <RowDivider mb={0} mt={0} />
          <FieldArray name="visibilityConfig">
            {(arrayHelpers) => (
              <>
                {values.visibilityConfig.map((rule, index) => {
                  if (rule.ruleType === VisibilityRuleType.NoVisibility) {
                    return null;
                  }

                  const { ruleKey } = rule;
                  return (
                    <div key={`visibility-rule-${ruleKey}`}>
                      <BaseTypography fontType="13Medium" textColor={DarkFont}>
                        {index === 0 ? 'Show if' : 'OR'}
                      </BaseTypography>
                      <AppVisibilityRule
                        ruleIndex={index}
                        onDeleteRule={() => {
                          arrayHelpers.remove(index);
                        }}
                      />
                    </div>
                  );
                })}

                <div>
                  <Button
                    className={classes.addRuleBtn}
                    onClick={() => {
                      if (values.visibilityConfig.length < 10) {
                        arrayHelpers.push(getDefaultVisibilityRule());
                      }
                    }}
                  >
                    Add OR rule
                  </Button>
                </div>
              </>
            )}
          </FieldArray>
        </div>
      )}
    </div>
  );
};

export const AppVisibilityForm: React.FC<Props> = ({
  onCloseModal,
  open,
  moduleVisbilityConfig,
  onVisibilityConfigSaved,
}) => {
  // If a module has a visibility config, it's visibility should be set to custom
  const initialFormValues = useMemo((): AllVisibilityRules => {
    if (moduleVisbilityConfig.length > 0) {
      if (
        moduleVisbilityConfig.at(0)?.ruleType ===
        VisibilityRuleType.NoVisibility
      ) {
        return {
          visibility: VisibilityType.NoVisibility,
          visibilityConfig: [],
        };
      }
      return {
        visibility: VisibilityType.CustomVisibility,
        visibilityConfig: moduleVisbilityConfig.map((rule) => ({
          ...rule,
          ruleKey: generateUniqueId(),
        })),
      };
    }
    return {
      visibility: VisibilityType.VisibleToEveryone,
      visibilityConfig: [],
    };
  }, [moduleVisbilityConfig]);

  const handleClose = () => {
    onCloseModal();
  };

  const handleSaveAppVisibility = async (values: AllVisibilityRules) => {
    await onVisibilityConfigSaved(values);
  };

  return (
    <FormModal<AllVisibilityRules>
      key="visibility-modal"
      modalProps={{
        width: 800,
        disableGutters: true,
        open,
      }}
      initialFormValue={initialFormValues}
      title="Visibility"
      handleSave={handleSaveAppVisibility}
      onClose={handleClose}
      useFormHook={useVisibilityForm}
      open={open}
    />
  );
};
